import React from "react";
import {
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBBtn,
  MDBInput,
  MDBBadge,
} from "mdb-react-ui-kit";
import { doFetch } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";

/**
 * Modal para confirmar o rechazar una ocurrencia pendiente de gasto periódico.
 *
 * Props:
 *  - show:            boolean
 *  - ocurrencia:      objeto { IDOcurrencia, concepto, importeEstimado, periodo,
 *                              periodicidad, nombreCuenta }
 *  - onClose():       callback al cerrar sin acción
 *  - onConfirmada():  callback tras confirmar con éxito
 *  - onRechazada():   callback tras rechazar con éxito
 *  - onLoading(bool): callback para spinner externo
 */
export class ConfirmarOcurrenciaModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.buildInitialState(props.ocurrencia);
  }

  buildInitialState(ocurrencia) {
    const today = new Date().toISOString().split("T")[0];
    return {
      importeReal: ocurrencia ? String(ocurrencia.importeEstimado ?? "") : "",
      fechaCargo:  today,
      errorMsg:    "",
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.ocurrencia !== this.props.ocurrencia ||
        prevProps.show !== this.props.show) {
      this.setState(this.buildInitialState(this.props.ocurrencia));
    }
  }

  formatCurrency(value) {
    return new Intl.NumberFormat("es-ES", {
      style: "currency", currency: "EUR",
    }).format(value ?? 0);
  }

  formatPeriodo(periodoStr) {
    if (!periodoStr) return "";
    try {
      const d = new Date(periodoStr);
      return d.toLocaleDateString("es-ES", { month: "long", year: "numeric" });
    } catch {
      return periodoStr;
    }
  }

  async doConfirmar() {
    const { importeReal, fechaCargo } = this.state;
    const { ocurrencia } = this.props;

    if (!importeReal || isNaN(parseFloat(importeReal)) || parseFloat(importeReal) <= 0) {
      this.setState({ errorMsg: "El importe real debe ser un número positivo." });
      return;
    }
    if (!fechaCargo) {
      this.setState({ errorMsg: "La fecha de cargo es obligatoria." });
      return;
    }

    this.setState({ errorMsg: "" });
    this.props.onLoading(true);

    const requestOptions = {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        idOcurrencia: ocurrencia.IDOcurrencia || ocurrencia.iDOcurrencia,
        importeReal: parseFloat(importeReal),
        fechaCargo,
      }),
    };

    await doFetch("CCON", "confirmarOcurrencia", requestOptions)
      .then((r) => r.json())
      .then((data) => {
        if (data.STATE === "SUCCESS") {
          this.props.onConfirmada();
        } else {
          this.setState({ errorMsg: data.MESSAGE || "Error al confirmar la ocurrencia." });
        }
      })
      .catch(() => this.setState({ errorMsg: "Error de conexión." }))
      .finally(() => this.props.onLoading(false));
  }

  async doRechazar() {
    const { ocurrencia } = this.props;
    this.setState({ errorMsg: "" });
    this.props.onLoading(true);

    const requestOptions = {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        idOcurrencia: ocurrencia.IDOcurrencia || ocurrencia.iDOcurrencia,
      }),
    };

    await doFetch("CCON", "rechazarOcurrencia", requestOptions)
      .then((r) => r.json())
      .then((data) => {
        if (data.STATE === "SUCCESS") {
          this.props.onRechazada();
        } else {
          this.setState({ errorMsg: data.MESSAGE || "Error al rechazar la ocurrencia." });
        }
      })
      .catch(() => this.setState({ errorMsg: "Error de conexión." }))
      .finally(() => this.props.onLoading(false));
  }

  render() {
    const { show, ocurrencia } = this.props;
    const { importeReal, fechaCargo, errorMsg } = this.state;

    if (!ocurrencia) return null;

    return (
      <MDBModal show={show} onHide={() => this.props.onClose()} tabIndex="-1">
        <MDBModalDialog size="md">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>Gasto periódico pendiente</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={() => this.props.onClose()} />
            </MDBModalHeader>

            <MDBModalBody>
              {/* Resumen de la ocurrencia */}
              <div className="p-3 mb-3 border rounded bg-light">
                <div className="d-flex justify-content-between align-items-start mb-1">
                  <span className="fw-semibold">{ocurrencia.concepto}</span>
                  <MDBBadge color="warning" light>
                    {this.formatPeriodo(ocurrencia.periodo)}
                  </MDBBadge>
                </div>
                <div className="text-muted small mb-1">
                  Cuenta: <strong>{ocurrencia.nombreCuenta}</strong>
                </div>
                <div className="text-muted small mb-1">
                  Periodicidad: <strong>{ocurrencia.periodicidad}</strong>
                </div>
                <div className="text-muted small">
                  Importe estimado:{" "}
                  <strong>{this.formatCurrency(ocurrencia.importeEstimado)}</strong>
                </div>
              </div>

              {/* Campos de confirmación */}
              <div className="mb-3">
                <MDBInput
                  label="Importe real (€) *"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={importeReal}
                  onChange={(e) => this.setState({ importeReal: e.target.value })}
                />
                <div className="form-text text-muted">
                  Puedes ajustarlo si difiere del estimado.
                </div>
              </div>

              <div className="mb-3">
                <MDBInput
                  label="Fecha de cargo *"
                  type="date"
                  value={fechaCargo}
                  onChange={(e) => this.setState({ fechaCargo: e.target.value })}
                />
              </div>

              {errorMsg && (
                <div className="alert alert-danger py-2">{errorMsg}</div>
              )}
            </MDBModalBody>

            <MDBModalFooter className="d-flex justify-content-between">
              <MDBBtn color="danger" outline onClick={() => this.doRechazar()}>
                Rechazar
              </MDBBtn>
              <div>
                <MDBBtn color="secondary" outline className="me-2" onClick={() => this.props.onClose()}>
                  Cancelar
                </MDBBtn>
                <MDBBtn color="success" onClick={() => this.doConfirmar()}>
                  Confirmar y registrar
                </MDBBtn>
              </div>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    );
  }
}
